import { useState, useEffect, useRef, useCallback } from "react";
import { sanitizeHtml } from '@/lib/sanitize-html';
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { detectFirstUrl, neutralizeCidImagesHtml } from "@/lib/pulse-link";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
// Removed legacy import - using direct React Query instead
import { Mail, Paperclip, CheckSquare, Square, Palette, List, X, AlertTriangle, Pencil, Check, ChevronDown, ChevronUp, Loader2, FileText, Plus, Phone } from "lucide-react";
import { CallPulseCard } from "@/components/calls/CallPulseCard";
import {
  CallCooperativeDialog,
  CallCooperativeDialogContent,
} from "@/components/calls/CallCooperativeDialog";
import { useCall } from "@/contexts/call-context";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PulseReactionsInline } from "@/components/pulse-reactions";
import { PulseSequenceTags } from "@/components/pulse-sequence-tags";
import { EmailPulseParticipants } from "@/components/email-pulse-participants";
import { useExpansion } from "@/contexts/expansion-context";
import { RichTextEditor, stripTransientImageUrls } from '@/components/ui/rich-text-editor';
import { cn, linkifyHtml } from "@/lib/utils";
import { logger } from "@/services/debug-logger";
import { PulseReplyButton } from "@/components/pulse-reply-button";
import { PulseForwardButton } from "@/components/pulse-forward-button";
import { EmailHtmlView } from "@/components/email-html-view";

interface TaskDetailDialogProps {
  task: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TaskDetailDialog({ task, open, onOpenChange }: TaskDetailDialogProps) {
  useEffect(() => {
    if (open && task && 'pulseDescription' in task) {
      void import('@/lib/pulse-user-tags').then(m => m.recordPulseRead(task.id));
    }
  }, [open, task?.id]);
  const { activeCall } = useCall();
  const [detailDescription, setDetailDescription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const notesEntityRef = useRef<string | null>(null);
  const notesSessionRef = useRef<object | null>(null);
  // REMOVED: isTaskCreationMode toggle - now always show draft UI for pulses
  const [editingStagedTaskId, setEditingStagedTaskId] = useState<string | null>(null);
  const [editingStagedTaskText, setEditingStagedTaskText] = useState("");
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingTaskText, setEditingTaskText] = useState("");
  const [internalOpen, setInternalOpen] = useState(open);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [stagedTasks, setStagedTasksInternal] = useState<Array<{
    id: string;
    taskDescription: string;
    status: string;
    dueDate?: Date;
    messageId?: number;
  }>>([]); 
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [textColorOverride, setTextColorOverride] = useState<'original' | 'white' | 'black'>('original');
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedLinkTitle, setEditedLinkTitle] = useState("");
  const [isEditingFullText, setIsEditingFullText] = useState(false);
  const [editedFullText, setEditedFullText] = useState("");
  const [isDetailedNotesExpanded, setIsDetailedNotesExpanded] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const imageUploadPendingRef = useRef(false);
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const taskCreationRef = useRef(false);
  const blockExternalClose = useRef(false);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { expandPulse, expandedItems } = useExpansion();

  
  // Sync internal state with prop but prevent unwanted closures
  useEffect(() => {
    // Don't auto-close if we have staged tasks
    if (stagedTasks.length > 0) {
      blockExternalClose.current = true;
    } else {
      blockExternalClose.current = false;
    }
    
    // Only allow external open if not blocked
    if (open && !internalOpen && !blockExternalClose.current) {
      setInternalOpen(true);
    }
    // Only allow external close if not blocked
    if (!open && internalOpen && !blockExternalClose.current) {
      // Don't close if we're in the middle of task creation or have staged tasks
      if (!isCreatingTask && !taskCreationRef.current && stagedTasks.length === 0) {
        setInternalOpen(false);
        // Reset title editing state when dialog closes
        setIsEditingTitle(false);
        setEditedTitle('');
      }
    }
  }, [open, internalOpen, isCreatingTask, stagedTasks.length]);
  
  
  // Check if this is a pulse or task by looking for pulseDescription
  const isPulse = task && 'pulseDescription' in task;
  
  // Generate storage keys based on entity type and ID
  const storageKeyPrefix = task?.id ? `${isPulse ? 'pulse' : 'task'}-${task.id}` : null;
  const stagedTasksKey = storageKeyPrefix ? `stagedTasks-${storageKeyPrefix}` : null;
  
  // Helper function to manage staged tasks with localStorage persistence
  const setStagedTasks = (updater: ((prev: Array<any>) => Array<any>) | Array<any>) => {
    setStagedTasksInternal(prev => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater;
      // Persist to localStorage
      if (stagedTasksKey) {
        if (newValue.length > 0) {
          localStorage.setItem(stagedTasksKey, JSON.stringify(newValue));
        } else {
          localStorage.removeItem(stagedTasksKey);
        }
      }
      return newValue;
    });
  };
  
  // Fetch full pulse details when dialog opens (includes htmlContent) - only for pulses
  // CRITICAL: placeholderData prevents undefined during refetch, avoiding component unmount
  const { data: fullPulseData, isLoading: pulseLoading, isPlaceholderData: pulsePlaceholder } = useQuery({
    queryKey: ['/api/pulses', task?.id, 'details'],
    enabled: open && !!task?.id && isPulse,
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,  // Keep old data during refetch
  });
  
  // Fetch full task details when dialog opens (includes htmlContent) - only for tasks
  // Only fetch if htmlContent is missing/empty (lite data from tree cache may have null/undefined)
  const isLiteTaskData = !isPulse && task && !task?.htmlContent;
  const { data: fullTaskData, isLoading: taskLoading, isPlaceholderData: taskPlaceholder } = useQuery({
    queryKey: ['/api/tasks', task?.id],
    enabled: open && !!task?.id && !isPulse && isLiteTaskData,
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
  
  // Use full data if available, fallback to passed task data
  // For pulses: use fullPulseData, for tasks: use fullTaskData if fetched
  const pulseData = isPulse 
    ? ((!pulsePlaceholder && fullPulseData) || task)
    : ((!taskPlaceholder && fullTaskData) || task);

  // Freeze the whole notes edit session, not just HTML that appears dirty:
  // Quill normalization is not a reliable indication of user changes.
  useEffect(() => {
    const entityChanged = notesEntityRef.current !== storageKeyPrefix;
    if (entityChanged) {
      notesEntityRef.current = storageKeyPrefix;
      notesSessionRef.current = null;
      setIsEditing(false);
    }
    if (entityChanged || (open && !isEditing)) {
      setDetailDescription(pulseData?.detailedNotes || '');
    }
  }, [storageKeyPrefix, open, pulseData, isEditing]);
  
  // Restore saved preferences independently of the notes edit session.
  useEffect(() => {
    if (open && storageKeyPrefix) {
      // Restore saved text color preference
      const savedColor = localStorage.getItem(`textColor-${storageKeyPrefix}`);
      if (savedColor === 'white' || savedColor === 'black' || savedColor === 'original') {
        setTextColorOverride(savedColor);
      }
      
      // Load staged tasks from localStorage
      if (stagedTasksKey) {
        try {
          const savedStagedTasks = localStorage.getItem(stagedTasksKey);
          if (savedStagedTasks) {
            const parsed = JSON.parse(savedStagedTasks);
            // Convert date strings back to Date objects
            const restoredTasks = parsed.map((task: any) => ({
              ...task,
              dueDate: task.dueDate ? new Date(task.dueDate) : undefined
            }));
            setStagedTasksInternal(restoredTasks);
          }
        } catch (error) {
          console.error('Failed to restore staged tasks:', error);
        }
      }
      
      // Restore saved scroll position after a slight delay to ensure content is rendered
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const savedScrollPos = localStorage.getItem(`scrollPos-${storageKeyPrefix}`);
          if (savedScrollPos) {
            scrollContainerRef.current.scrollTop = parseInt(savedScrollPos, 10);
          }
          updateScrollIndicators();
        }
      }, 100);
    }
  }, [task, open, pulseData, isPulse, storageKeyPrefix]);
  
  // Save text color preference when it changes
  useEffect(() => {
    if (storageKeyPrefix && open) {
      localStorage.setItem(`textColor-${storageKeyPrefix}`, textColorOverride);
    }
  }, [textColorOverride, storageKeyPrefix, open]);
  
  // Update scroll indicators
  const updateScrollIndicators = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    
    // Show top indicator if scrolled down
    setShowTopIndicator(scrollTop > 5);
    
    // Show bottom indicator if there's more content below
    setShowBottomIndicator(scrollHeight - scrollTop - clientHeight > 5);
  };
  
  // Handle scroll events and save position
  const handleScroll = () => {
    updateScrollIndicators();
    
    if (storageKeyPrefix && scrollContainerRef.current) {
      const scrollPos = scrollContainerRef.current.scrollTop;
      localStorage.setItem(`scrollPos-${storageKeyPrefix}`, scrollPos.toString());
    }
  };

  // Cleanup auto-close timeout on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, []);

  // Mutation to update task/pulse status
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const endpoint = isPulse ? `/api/pulses/${task.id}` : `/api/tasks/${task.id}`;
      const response = await apiRequest('PATCH', endpoint, { status: newStatus });
      return response.json();
    },
    onSuccess: () => {
      // Don't invalidate years query to prevent dialog flickering
      if (isPulse) {
        queryClient.invalidateQueries({ queryKey: ['/api/pulses', task.id, 'details'] });
      }
      toast({
        title: "Status Updated",
        description: `${isPulse ? 'Pulse' : 'Task'} status has been updated successfully.`,
      });
    },
    onError: (error) => {      
      toast({
        title: "Status Update Failed",
        description: "Unable to update status. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to update individual task status in the task list
  const updateTaskStatusMutation = useMutation({
    mutationFn: async ({ taskId, newStatus }: { taskId: number; newStatus: string }) => {
      const response = await apiRequest('PATCH', `/api/tasks/${taskId}`, { status: newStatus });
      return response.json();
    },
    onSuccess: () => {
      // Only refresh pulse details to update the task list
      if (isPulse) {
        queryClient.invalidateQueries({ queryKey: ['/api/pulses', task.id, 'details'] });
      }
      // Don't invalidate years to prevent dialog closing
    },
    onError: (error) => {      
      toast({
        title: "Status Update Failed",
        description: "Unable to update task status. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to update individual task description (inline edit in the
  // committed task list). Uses the same PATCH /api/tasks/:id endpoint as
  // status updates; only the pulse details query is invalidated so the
  // dialog itself doesn't reload.
  const updateTaskDescriptionMutation = useMutation({
    mutationFn: async ({ taskId, taskDescription }: { taskId: number; taskDescription: string }) => {
      const response = await apiRequest('PATCH', `/api/tasks/${taskId}`, { taskDescription });
      return response.json();
    },
    onSuccess: () => {
      if (isPulse) {
        queryClient.invalidateQueries({ queryKey: ['/api/pulses', task.id, 'details'] });
      }
      setEditingTaskId(null);
      setEditingTaskText("");
      toast({
        title: "Task updated",
        description: "Task text saved.",
      });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Task text could not be saved.",
        variant: "destructive",
      });
    },
  });

  const handleSaveTaskEdit = (taskId: number) => {
    const trimmed = editingTaskText.trim();
    if (!trimmed) {
      toast({
        title: "Empty task text",
        description: "Please enter text before saving.",
        variant: "destructive",
      });
      return;
    }
    updateTaskDescriptionMutation.mutate({ taskId, taskDescription: trimmed });
  };

  // Mutation to update pulse title (plus optional link metadata override).
  //
  // Title and link metadata are written via TWO separate PATCH endpoints
  // so the link write can do an atomic server-side jsonb merge (touching
  // only `linkUrl` + `linkTitle`) — that means a background call-pulse /
  // AI-summary job that flips `metadata.ai_summary_status` while the
  // edit dialog is open will NOT be clobbered by the save click.
  // The two calls fire in parallel; either one failing surfaces as a
  // mutation error.
  const updatePulseTitleMutation = useMutation({
    mutationFn: async (vars: { newTitle: string; linkTitleOverride: string }) => {
      const detectedUrl = detectFirstUrl(vars.newTitle);
      const trimmedLinkTitle = vars.linkTitleOverride.trim();
      const [titleRes] = await Promise.all([
        apiRequest('PATCH', `/api/pulses/${task.id}`, {
          pulseDescription: vars.newTitle,
        }),
        apiRequest('PATCH', `/api/pulses/${task.id}/link-metadata`, {
          linkUrl: detectedUrl,
          linkTitle: detectedUrl && trimmedLinkTitle ? trimmedLinkTitle : null,
        }),
      ]);
      return titleRes.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', task.id, 'details'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', task.projectId, 'pulses'] });
      setIsEditingTitle(false);
      toast({
        title: "Title Updated",
        description: "Pulse title has been saved successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Save Failed",
        description: "Unable to update pulse title. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to save detailed description
  const saveDescriptionMutation = useMutation({
    mutationFn: async (newDescription: string) => {
      const target = { id: task.id, pulseId: task.pulseId, isPulse, source: pulseData, session: notesSessionRef.current };
      const endpoint = isPulse ? `/api/pulses/${task.id}` : `/api/tasks/${task.id}`;
      // Save to detailedNotes field instead of main description
      const detailedNotes = stripTransientImageUrls(newDescription);
      const response = await apiRequest('PATCH', endpoint, { detailedNotes });
      const saved = await response.json();
      return { target, detailedNotes: saved.detailedNotes ?? detailedNotes };
    },
    onSuccess: ({ target, detailedNotes }) => {
      const detailKey = target.isPulse
        ? ['/api/pulses', target.id, 'details']
        : ['/api/tasks', target.id];
      // Publish the accepted notes before ending the session; an old cache
      // snapshot must not flash back into the view while refetch is pending.
      queryClient.setQueryData(detailKey, (old: any) => ({ ...(old || target.source), detailedNotes }));
      // Don't invalidate years query to prevent dialog flickering
      queryClient.invalidateQueries({ queryKey: detailKey });
      if (!target.isPulse) {
        // For tasks, invalidate task-specific queries and parent pulse queries
        if (target.pulseId) {
          queryClient.invalidateQueries({ queryKey: ['/api/pulses', target.pulseId, 'details'] });
        }
      }
      if (notesSessionRef.current === target.session) {
        notesSessionRef.current = null;
        setIsEditing(false);
      }
      toast({
        title: "Description Updated",
        description: `${target.isPulse ? 'Pulse' : 'Task'} description has been saved successfully.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Save Failed",
        description: "Unable to save description. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to save full text content
  const saveFullTextMutation = useMutation({
    mutationFn: async (newHtmlContent: string) => {
      const response = await apiRequest('PATCH', `/api/pulses/${task.id}`, { htmlContent: stripTransientImageUrls(newHtmlContent) });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', task.id, 'details'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', task.projectId, 'pulses'] });
      setIsEditingFullText(false);
      toast({
        title: "Full Text Updated",
        description: "Pulse content has been saved successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Save Failed",
        description: "Unable to save full text. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Handle checkbox toggle
  const handleStatusToggle = () => {
    const currentStatus = task.status || 'empty';
    const newStatus = (currentStatus === 'done' || currentStatus === 'Done') ? 'working_on_it' : 'done';
    updateStatusMutation.mutate(newStatus);
  };

  // Save description changes
  const handleSaveDescription = () => {
    saveDescriptionMutation.mutate(detailDescription);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    // Restore original detailed notes from the correct data source (pulseData includes fullTaskData for tasks)
    const originalNotes = pulseData?.detailedNotes || '';
    setDetailDescription(originalNotes);
    notesSessionRef.current = null;
    setIsEditing(false);
  };

  // Save full text changes
  const handleSaveFullText = () => {
    if (imageUploadPendingRef.current) {
      toast({
        title: "Image upload in progress",
        description: "Wait for the image upload to finish before saving.",
        variant: "destructive",
      });
      return;
    }
    saveFullTextMutation.mutate(editedFullText);
  };

  const handleImageUploadPendingChange = useCallback((pending: boolean) => {
    imageUploadPendingRef.current = pending;
    setImageUploadPending(pending);
  }, []);

  // Start editing full text
  const handleStartEditFullText = () => {
    // Initialize with current HTML content or convert plain text to HTML (pulseData includes fullTaskData for tasks)
    const currentHtml = pulseData?.htmlContent || `<p>${pulseData?.pulseDescription || ''}</p>`;
    setEditedFullText(currentHtml);
    setIsEditingFullText(true);
  };

  // Cancel editing full text
  const handleCancelEditFullText = () => {
    setIsEditingFullText(false);
    setEditedFullText("");
  };

  // Start editing pulse title
  const handleStartEditTitle = () => {
    setEditedTitle(pulseData?.pulseDescription || '');
    const meta = (pulseData?.metadata ?? {}) as Record<string, unknown>;
    setEditedLinkTitle(typeof meta.linkTitle === 'string' ? meta.linkTitle : '');
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  // Save pulse title
  const handleSaveTitle = () => {
    if (editedTitle.trim()) {
      updatePulseTitleMutation.mutate({
        newTitle: editedTitle.trim(),
        linkTitleOverride: editedLinkTitle,
      });
    }
  };

  // Cancel title editing
  const handleCancelTitleEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
    setEditedLinkTitle('');
  };

  // Parse task description to extract title and strip HTML for display
  const parseTaskDescription = (taskDescription: string) => {
    if (!taskDescription) return { title: "", fullTitle: "", description: "" };
    
    // Strip HTML tags for plain text display
    const stripHtml = (html: string) => {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    };
    
    const lines = taskDescription.split('\n');
    const fullTitle = stripHtml(lines[0] || "");
    
    // Truncate title for display if too long (max 100 characters)
    const maxTitleLength = 100;
    let displayTitle = fullTitle;
    if (fullTitle.length > maxTitleLength) {
      displayTitle = fullTitle.substring(0, maxTitleLength) + '...';
    }
    
    const descriptionLines = lines.slice(2); // Skip empty line after title
    const descriptionText = stripHtml(descriptionLines.join('\n').trim());
    
    return {
      title: displayTitle,
      fullTitle: fullTitle,  // Keep full title for Full Text section
      description: descriptionText
    };
  };

  const handleClose = () => {
    if (stagedTasks.length > 0) {
      setShowCloseWarning(true);
      return;
    }
    
    // If this is a pulse dialog closing, refresh all year data to show newly created tasks
    // Don't invalidate years query here to prevent flickering
    
    setInternalOpen(false);
    onOpenChange(false);
  };

  // Handle staging a task instead of creating it immediately
  const handleStageTask = (taskData: { taskDescription: string; status: string; dueDate?: Date; messageId?: number }) => {
    const stagedTask = {
      id: `staged-${Date.now()}-${Math.random()}`,
      ...taskData
    };
    setStagedTasks(prev => [...prev, stagedTask]);
    
    toast({
      title: "Task drafted",
      description: "Added to draft list. Edit if needed, then click 'Create Tasks'.",
      duration: 2000
    });
  };

  // Remove a staged task
  const handleRemoveStagedTask = (taskId: string) => {
    setStagedTasks(prev => prev.filter(task => task.id !== taskId));
  };

  // Create all staged tasks with optimized cache updates
  const handleCreateAllStagedTasks = async () => {
    logger.info('TASK_CREATE_ALL', 'Create Tasks button clicked', {
      stagedTaskCount: stagedTasks.length,
      pulseId: pulseData?.id
    });
    
    // Compute final task list, applying any in-progress edit
    let finalTasks = stagedTasks;
    if (editingStagedTaskId && editingStagedTaskText.trim()) {
      finalTasks = stagedTasks.map(task => 
        task.id === editingStagedTaskId 
          ? { ...task, taskDescription: editingStagedTaskText.trim() }
          : task
      );
      // Clear edit state
      setEditingStagedTaskId(null);
      setEditingStagedTaskText("");
    }
    
    if (!pulseData || finalTasks.length === 0) {
      logger.warn('TASK_CREATE_ALL', 'Create Tasks aborted - no pulse data or tasks', {
        hasPulseData: !!pulseData,
        stagedTaskCount: finalTasks.length
      });
      return;
    }
    
    try {
      // Fetch pulse sequences once (all tasks will copy from the same pulse)
      let pulseSequences: any[] = [];
      try {
        const pulseSequencesResponse = await apiRequest('GET', `/api/pulses/${pulseData.id}/sequences`);
        if (pulseSequencesResponse.ok) {
          pulseSequences = await pulseSequencesResponse.json();
        }
      } catch (error) {
        console.error('Failed to fetch pulse sequences:', error);
      }

      // Batch create all tasks in parallel
      const taskPromises = finalTasks.map(async (task) => {
        const taskData = {
          projectId: pulseData.projectId,
          pulseId: pulseData.id,
          taskDescription: task.taskDescription.trim(),
          status: task.status,
          dueDate: task.dueDate ? task.dueDate.toISOString() : undefined,
          category: 'internal' as const
        };
        
        const response = await apiRequest('POST', '/api/tasks', taskData);
        const createdTask = await response.json();
        
        // Copy pulse sequence assignments to the new task (if any)
        if (pulseSequences.length > 0) {
          await Promise.all(
            pulseSequences.map(async (pulseSeq: any) => {
              try {
                await apiRequest('POST', `/api/tasks/${createdTask.id}/sequences`, {
                  sequenceNumber: pulseSeq.sequenceNumber
                });
              } catch (error) {
                console.error(`Failed to assign sequence ${pulseSeq.sequenceNumber}:`, error);
              }
            })
          );
        }
        
        return createdTask;
      });

      let createdTasks: any[];
      try {
        createdTasks = await Promise.all(taskPromises);
      } finally {
        // Runs even on partial failure: some parallel POSTs may have
        // succeeded server-side, and those rows must reach the main
        // table view (which reads the tree snapshot) promptly.
        queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      }
      
      // Surgically update pulse cache with new tasks (no global invalidation)
      queryClient.setQueryData(['/api/pulses', pulseData.id, 'details'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          tasks: [...(old.tasks || []), ...createdTasks]
        };
      });
      
      // Update has-tasks flag
      queryClient.setQueryData(['/api/pulses', pulseData.id, 'has-tasks'], { hasTasks: true });
      
      // Update batch metadata cache to reflect new task counts
      queryClient.setQueryData(['/api/pulses/batch-metadata'], (old: any) => {
        if (!old || !old[pulseData.id]) return old;
        
        const pulseMetadata = old[pulseData.id];
        return {
          ...old,
          [pulseData.id]: {
            ...pulseMetadata,
            hasTasks: true,
            taskCounts: {
              ...pulseMetadata.taskCounts,
              total: (pulseMetadata.taskCounts?.total || 0) + createdTasks.length
            }
          }
        };
      });
      
      // Update batch summary cache
      queryClient.setQueryData(['/api/pulses/batch/summary'], (old: any) => {
        if (!old || !old[pulseData.id]) return old;
        
        const pulseSummary = old[pulseData.id];
        return {
          ...old,
          [pulseData.id]: {
            ...pulseSummary,
            hasTasks: true,
            totals: {
              ...pulseSummary.totals,
              total: (pulseSummary.totals?.total || 0) + createdTasks.length
            }
          }
        };
      });
      
      // Clear staged tasks and localStorage
      setStagedTasks([]);
      if (stagedTasksKey) {
        localStorage.removeItem(stagedTasksKey);
      }
      
      logger.info('TASK_CREATE_ALL', 'Tasks created successfully', {
        count: createdTasks.length
      });
      
      toast({
        title: "Tasks created",
        description: `Successfully created ${finalTasks.length} task${finalTasks.length !== 1 ? 's' : ''}.`
      });
      
      // Only expand pulse if it's not already expanded (preserve visibility without flickering)
      if (!expandedItems[`pulse-${pulseData.id}`]) {
        expandPulse(pulseData.id);
      }
      
      logger.info('TASK_CREATE_ALL', 'Initiating auto-close', {
        delayMs: 500
      });
      
      // Auto-close dialog after successful task creation
      autoCloseTimeoutRef.current = setTimeout(() => {
        logger.info('TASK_CREATE_ALL', 'Auto-close executing');
        // Directly close dialog - don't use handleClose() to avoid closure trap with stagedTasks
        setInternalOpen(false);
        onOpenChange(false);
      }, 500); // Small delay to let user see the success toast
      
    } catch (error) {
      console.error('Failed to create staged tasks:', error);
      toast({
        title: "Error",
        description: "Failed to create tasks. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle adding selected text to draft list (Task Creation Mode)
  const handleAddSelectionToDraft = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      toast({
        title: "No text selected",
        description: "Please select some text first.",
        variant: "destructive"
      });
      return;
    }
    
    // Get and sanitize selected text
    let selectedTextValue = selection.toString().trim();
    
    // Normalize whitespace: collapse multiple spaces/newlines into single space
    selectedTextValue = selectedTextValue.replace(/\s+/g, ' ').trim();
    
    if (selectedTextValue.length < 3) {
      toast({
        title: "Selection too short",
        description: "Please select at least 3 characters.",
        variant: "destructive"
      });
      return;
    }
    
    // Truncate very long selections to prevent oversized tasks
    const maxLength = 500;
    if (selectedTextValue.length > maxLength) {
      selectedTextValue = selectedTextValue.substring(0, maxLength) + '...';
    }
    
    // Add to staged tasks with default status
    handleStageTask({
      taskDescription: selectedTextValue,
      status: 'Working on it'
    });
    
    // Clear selection
    selection.removeAllRanges();
  };
  
  // Handle saving edited staged task
  const handleSaveStagedTaskEdit = (taskId: string) => {
    if (!editingStagedTaskText.trim()) {
      toast({
        title: "Cannot save empty task",
        description: "Please enter a task description.",
        variant: "destructive"
      });
      return;
    }
    
    setStagedTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, taskDescription: editingStagedTaskText.trim() }
        : task
    ));
    setEditingStagedTaskId(null);
    setEditingStagedTaskText("");
  };

  const downloadAttachment = async (attachment: any, _attachmentIndex: number) => {
    try {
      const response = await apiRequest('GET', `/api/pulses/${task.id}/attachments/download?path=${encodeURIComponent(attachment.path)}`);
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast({
        title: "Download Failed", 
        description: "Unable to download the attachment. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!task) return null;

  const taskOrPulseDescription = pulseData.taskDescription || pulseData.pulseDescription || '';
  const parsed = parseTaskDescription(taskOrPulseDescription);
  
  // Check if this pulse has rich content (email or manual) - now using fullPulseData
  // Priority: detailedNotes (Markdown) > htmlContent (Sanitized HTML) > referenceTextSection (Raw Fallback)
  const preferredContent = pulseData.detailedNotes || pulseData.htmlContent || pulseData.referenceTextSection;
  const hasRichContent = preferredContent && (pulseData.category === "email_in" || pulseData.category === "email_out" || pulseData.category === "internal");
  const hasAttachments = pulseData.attachments && pulseData.attachments.length > 0;
  
  // Check if this is a milestone
  const isMilestone = isPulse && pulseData.category === 'milestone';

  return (
    <>
      <CallCooperativeDialog
        cooperative={isPulse}
        callActive={isPulse && !!activeCall}
        open={internalOpen}
        onOpenChange={(newOpen) => {
        if (!newOpen && (isCreatingTask || taskCreationRef.current || blockExternalClose.current)) {
          // Prevent closing if creating tasks
          logger.debug('TASK_DETAIL', 'Dialog close prevented - creating task', {
            isCreatingTask,
            taskCreationRef: taskCreationRef.current,
            blockExternalClose: blockExternalClose.current
          });
          return;
        }
        logger.debug('TASK_DETAIL', 'Dialog state change allowed', { newOpen });
        setInternalOpen(newOpen);
        
        // When closing a pulse dialog, only invalidate the pulse details
        if (!newOpen && isPulse) {
          logger.debug('TASK_DETAIL', 'Pulse dialog closing, refreshing pulse details');
          // Only invalidate the specific pulse details to ensure fresh data
          queryClient.invalidateQueries({ queryKey: ['/api/pulses', task?.id, 'details'] });
          // Don't invalidate years to prevent flickering
        }
        
        // Only notify parent after protection period
        if (!blockExternalClose.current) {
          onOpenChange(newOpen);
        }
      }}
      >
        <CallCooperativeDialogContent
          className="max-w-5xl max-h-[90vh] navy-card border-gray-600"
          aria-labelledby="task-detail-dialog-title"
          aria-describedby="task-detail-dialog-description"
        >
        <DialogTitle id="task-detail-dialog-title" className="sr-only">
          {parsed.title || "Details"}
        </DialogTitle>
        <DialogDescription id="task-detail-dialog-description" className="sr-only">
          {isPulse ? 'Pulse details and task management' : 'Task details and status'}
        </DialogDescription>
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            {/* Title and Checkbox Section */}
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 mt-1">
                {!isPulse && (
                  <button
                    onClick={handleStatusToggle}
                    disabled={updateStatusMutation.isPending}
                    className="text-gray-400 hover:text-gray-200 transition-colors"
                  >
                    {(task.status === 'done' || task.status === 'Done') ? (
                      <CheckSquare className="w-5 h-5 text-green-500" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
              <div className="flex-1">
                {isEditingTitle && isPulse ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        ref={titleInputRef}
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveTitle();
                          } else if (e.key === 'Escape') {
                            handleCancelTitleEdit();
                          }
                        }}
                        className="text-xl font-semibold text-gray-100 bg-gray-800 border-gray-600"
                        data-testid="input-pulse-title"
                      />
                      <Button
                        size="sm"
                        onClick={handleSaveTitle}
                        disabled={updatePulseTitleMutation.isPending || !editedTitle.trim()}
                        className="flex-shrink-0"
                        data-testid="button-save-title"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelTitleEdit}
                        disabled={updatePulseTitleMutation.isPending}
                        className="flex-shrink-0"
                        data-testid="button-cancel-title"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {detectFirstUrl(editedTitle) && (
                      <div className="space-y-1" data-testid="link-title-block">
                        <Label htmlFor="edit-link-title" className="text-xs text-gray-400">
                          Link Title <span className="text-gray-500">(optional — shown instead of the URL)</span>
                        </Label>
                        <Input
                          id="edit-link-title"
                          value={editedLinkTitle}
                          onChange={(e) => setEditedLinkTitle(e.target.value)}
                          placeholder="e.g. Watch the cut"
                          className="bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500"
                          data-testid="input-edit-link-title"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group">
                    <DialogTitle className="text-xl font-semibold text-gray-100 break-all">
                      {parsed.title || "Details"}
                    </DialogTitle>
                    {isPulse && (
                      <button
                        onClick={handleStartEditTitle}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-200 transition-all flex-shrink-0"
                        data-testid="button-edit-pulse-title"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                {isPulse && task.projectId && task.category !== "email_in" && (
                  <div className="mt-1">
                    <PulseReactionsInline
                      pulseId={task.id}
                      projectId={task.projectId}
                      maxVisible={4}
                    />
                  </div>
                )}
                {/* Project ID + Pulse/Task ID on one line */}
                <div className="flex items-baseline gap-2 mt-0.5">
                  {task.projectId && (
                    <span className="text-sm font-medium text-gray-200">
                      {task.projectId}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {isPulse ? 'Pulse' : 'Task'} ID: {task.id}
                  </span>
                </div>
                <div className="text-sm text-gray-400 mt-1 flex items-center gap-4">
                  <span>Created: {task.dateAdded ? new Date(task.dateAdded).toLocaleString() : 'Unknown'}</span>
                  {task.category === "email_in" && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4 text-purple-400" />
                      Email Content
                    </span>
                  )}
                  {task.category === "email_out" && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4 text-blue-400" />
                      Sent Email
                    </span>
                  )}
                  {task.category === "internal" && (
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-600" />
                      {isPulse ? 'Pulse' : 'Task'}
                    </span>
                  )}
                </div>
                {isPulse && (task.category === "email_in" || task.category === "email_out") && task.id && task.projectId && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {task.category === "email_in" && (
                      <PulseReplyButton pulseId={task.id} projectId={task.projectId} />
                    )}
                    <PulseForwardButton pulseId={task.id} projectId={task.projectId} />
                  </div>
                )}
                {/* Task #40: email participants with approve & role assignment */}
                {isPulse && (task.category === "email_in" || task.category === "email_out") && task.id && task.projectId && (
                  <div className="mt-2">
                    <EmailPulseParticipants
                      pulseId={task.id}
                      projectId={task.projectId}
                      direction={task.category === "email_out" ? "outgoing" : "incoming"}
                      defaultClientId={typeof task.projectId === 'string' && task.projectId.includes('-')
                        ? task.projectId.split('-')[1]?.slice(0, 2) || null
                        : null}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Sequence Tags - positioned right-aligned, compact design */}
            {/* Only show sequences if we have a valid pulseId (either from pulse or task) */}
            {task?.id && (isPulse || task?.pulseId) && (
              <div className="flex-shrink-0 w-64 mr-8">
                <PulseSequenceTags 
                  pulseId={isPulse ? task.id : task.pulseId} 
                  projectId={task.projectId}
                  className="sequence-tags-compact"
                  showInlineAllLink={true}
                />
              </div>
            )}
            
            {/* REMOVED: Task Creation Mode Toggle - now always allow drafting for pulses */}
          </div>
        </DialogHeader>
        
        <div className="space-y-6 overflow-y-auto max-h-[70vh]">
          {/* Phase E.2 (#192): call pulses render via CallPulseCard so the
              chrome (icon, date, duration, participants, admin regenerate)
              and embedded snapshot lightbox live in one place. Skips the
              generic Full Text card below — call HTML has its own structure
              (AI summary + transcript <details> + snapshots). */}
          {isPulse && pulseData.category === 'call' && pulseData.htmlContent && !isMilestone && (
            <CallPulseCard
              pulse={{
                id: pulseData.id,
                projectId: pulseData.projectId,
                pulseDescription: pulseData.pulseDescription,
                htmlContent: pulseData.htmlContent,
                callSessionId: pulseData.callSessionId ?? null,
                dateAdded: pulseData.dateAdded ?? null,
                metadata: pulseData.metadata ?? null,
              }}
            />
          )}

          {/* Full Task/Pulse Text Display - Hidden for milestones and call pulses */}
          {!isMilestone && !(isPulse && pulseData.category === 'call' && pulseData.htmlContent) && (
            <div className="border border-gray-600 rounded-md bg-gray-800">
              <div className="p-3 border-b border-gray-600 bg-gray-750 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-2">
                  Full Text
                  {isPulse && (
                    <span className="text-xs text-gray-400 font-normal">(Select text to add tasks)</span>
                  )}
                </h3>
              <div className="flex items-center gap-1 flex-wrap">
                {isEditingFullText && (
                  <>
                    <Button
                      size="sm"
                      onClick={handleSaveFullText}
                      disabled={saveFullTextMutation.isPending || imageUploadPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2 text-xs"
                    >
                      {saveFullTextMutation.isPending ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Saving...
                        </>
                      ) : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEditFullText}
                      disabled={saveFullTextMutation.isPending}
                      className="h-7 px-2 text-xs"
                    >
                      Cancel
                    </Button>
                  </>
                )}
                {!isEditingFullText && isPulse && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleStartEditFullText}
                    className="h-7 px-2 text-xs"
                    title="Edit full text"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={textColorOverride === 'original' ? 'default' : 'outline'}
                  onClick={() => setTextColorOverride('original')}
                  className="h-7 px-2 text-xs"
                  title="Original colors"
                  data-testid="button-color-original"
                >
                  <Palette className="w-3 h-3 mr-1" />
                  Original
                </Button>
                <Button
                  size="sm"
                  variant={textColorOverride === 'white' ? 'default' : 'outline'}
                  onClick={() => setTextColorOverride('white')}
                  className="h-7 px-2 text-xs"
                  title="All text white"
                  data-testid="button-color-white"
                >
                  White
                </Button>
                <Button
                  size="sm"
                  variant={textColorOverride === 'black' ? 'default' : 'outline'}
                  onClick={() => setTextColorOverride('black')}
                  className="h-7 px-2 text-xs"
                  title="All text black"
                  data-testid="button-color-black"
                >
                  Black
                </Button>
              </div>
            </div>
            <div className="relative">
              {isEditingFullText ? (
                <div className="p-4">
                  <div 
                    className={cn(
                      "quill-editor-wrapper rounded-md",
                      textColorOverride === 'white' && "quill-white-text",
                      textColorOverride === 'black' && "quill-black-text"
                    )}
                  >
                    <RichTextEditor
                      value={editedFullText}
                      onChange={setEditedFullText}
                      showImageButton={true}
                      pulseId={task.id}
                      onImageUploadPendingChange={handleImageUploadPendingChange}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* Top scroll indicator */}
                  {showTopIndicator && (
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-white opacity-60 z-10 pointer-events-none" />
                  )}
                  {/* Bottom scroll indicator */}
                  {showBottomIndicator && (
                    <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-white opacity-60 z-10 pointer-events-none" />
                  )}
                  <div 
                    ref={scrollContainerRef}
                    className="p-4 text-gray-100 max-h-[400px] overflow-y-auto cursor-text select-text break-all"
                    onScroll={handleScroll}
                  >
                    {hasRichContent && preferredContent ? (
                      <EmailHtmlView
                        key={task.id}
                        enhanceImages={isPulse && (pulseData.category === 'email_in' || pulseData.category === 'email_out')}
                        className={cn(
                          "prose prose-invert max-w-none prose-sm pulse-rich-content",
                          textColorOverride === 'white' && "text-color-override-white",
                          textColorOverride === 'black' && "text-color-override-black"
                        )}
                        html={sanitizeHtml(linkifyHtml(neutralizeCidImagesHtml(preferredContent)))}
                        style={{
                          '--tw-prose-body': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#f3f4f6',
                          '--tw-prose-headings': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#f9fafb',
                          '--tw-prose-links': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#60a5fa',
                          '--tw-prose-bold': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#f9fafb',
                          '--tw-prose-counters': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#9ca3af',
                          '--tw-prose-bullets': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#6b7280',
                          '--tw-prose-hr': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#374151',
                          '--tw-prose-quotes': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#e5e7eb',
                          '--tw-prose-quote-borders': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#374151',
                          '--tw-prose-captions': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#9ca3af',
                          '--tw-prose-code': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#f3f4f6',
                          '--tw-prose-pre-code': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#f3f4f6',
                          '--tw-prose-pre-bg': textColorOverride === 'black' ? '#f3f4f6' : '#1f2937',
                          '--tw-prose-th-borders': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#374151',
                          '--tw-prose-td-borders': textColorOverride === 'white' ? '#ffffff' : textColorOverride === 'black' ? '#000000' : '#374151',
                        } as React.CSSProperties}
                      />
                    ) : (
                      <div className={cn(
                        "whitespace-pre-wrap",
                        textColorOverride === 'white' && "text-white",
                        textColorOverride === 'black' && "text-black"
                      )}>
                        {/* Show full title first, then description */}
                        {parsed.fullTitle ? (
                          <>
                            {parsed.fullTitle}
                            {parsed.description && (
                              <>
                                {'\n\n'}
                                {parsed.description}
                              </>
                            )}
                          </>
                        ) : (
                          "No description available."
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Add to Draft Button - always visible for pulses */}
              {isPulse && (
                <div className="p-2 border-t border-gray-600 bg-gray-750 flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleAddSelectionToDraft}
                    className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                    data-testid="button-add-to-draft"
                  >
                    <Plus className="w-4 h-4" />
                    Add Selection to Draft
                  </Button>
                </div>
              )}
            </div>
        </div>
        )}

          {/* Rich Text Editor for Detailed Notes - Hidden for milestones, Collapsible */}
          {!isMilestone && (
            <Collapsible 
              open={isDetailedNotesExpanded || isEditing} 
              onOpenChange={(open) => {
                if (!isEditing) {
                  setIsDetailedNotesExpanded(open);
                }
              }}
            >
              <div className={cn(
                "border rounded-md bg-gray-800",
                detailDescription && !isDetailedNotesExpanded && !isEditing
                  ? "border-amber-600/50"
                  : "border-gray-600"
              )}>
                <CollapsibleTrigger asChild>
                  <div className={cn(
                    "p-3 flex items-center justify-between cursor-pointer hover:bg-gray-750 rounded-t-md",
                    detailDescription && !isDetailedNotesExpanded && !isEditing
                      ? "bg-amber-900/20"
                      : "bg-gray-750"
                  )}>
                    <div className="flex items-center gap-2">
                      <FileText className={cn(
                        "w-4 h-4",
                        detailDescription && !isDetailedNotesExpanded && !isEditing
                          ? "text-amber-400"
                          : "text-gray-400"
                      )} />
                      <h3 className={cn(
                        "text-sm font-semibold",
                        detailDescription && !isDetailedNotesExpanded && !isEditing
                          ? "text-amber-400"
                          : "text-gray-100"
                      )}>
                        Detailed Notes
                        {detailDescription && !isDetailedNotesExpanded && !isEditing && (
                          <span className="ml-2 text-xs font-normal text-amber-500">(has content)</span>
                        )}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveDescription();
                            }}
                            disabled={saveDescriptionMutation.isPending}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {saveDescriptionMutation.isPending ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Saving...
                              </>
                            ) : 'Save'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (isDetailedNotesExpanded || !detailDescription) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsDetailedNotesExpanded(true);
                            setDetailDescription(pulseData?.detailedNotes || '');
                            notesSessionRef.current = {};
                            setIsEditing(true);
                          }}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          Edit
                        </Button>
                      ) : null}
                      {isDetailedNotesExpanded || isEditing ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t border-gray-600 p-4">
                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="rich-text-editor">
                          <RichTextEditor
                            value={detailDescription}
                            onChange={setDetailDescription}
                            placeholder="Add detailed notes for this item..."
                            showImageButton={false}
                            style={{
                              backgroundColor: '#1f2937',
                              color: '#f3f4f6',
                              minHeight: '200px'
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="min-h-[100px] max-h-[400px] overflow-y-auto cursor-text select-text"
                      >
                        {detailDescription ? (
                          <EmailHtmlView
                            key={task.id}
                            enhanceImages={isPulse && (pulseData.category === 'email_in' || pulseData.category === 'email_out')}
                            className="prose prose-invert max-w-none"
                            html={sanitizeHtml(linkifyHtml(neutralizeCidImagesHtml(detailDescription)))}
                          />
                        ) : (
                          <div className="text-gray-400 italic p-4 text-center">
                            No detailed notes yet. Click "Edit" to add some.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Attachments Section */}
          {hasAttachments && (
            <div className="border border-gray-600 rounded-md p-4 bg-gray-800">
              <h3 className="text-sm font-semibold text-gray-100 mb-3 flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments ({pulseData.attachments.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {pulseData.attachments.map((attachment: any, idx: number) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                    onClick={() => downloadAttachment(attachment, idx)}
                  >
                    <Paperclip className="w-3 h-3 mr-1" />
                    {attachment.filename} ({Math.round(attachment.size / 1024)}KB)
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Tasks Section - only for pulses */}
          {isPulse && (fullPulseData as any)?.tasks && (fullPulseData as any).tasks.length > 0 && (
            <div className="border border-gray-600 rounded-md bg-gray-800">
              <div className="p-3 border-b border-gray-600 bg-gray-750">
                <h3 className="text-sm font-semibold text-gray-100">
                  Tasks ({(fullPulseData as any).tasks.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-700">
                {(fullPulseData as any).tasks.map((task: any) => {
                  const isEditingThisTask = editingTaskId === task.id;
                  const isDone = task.status === 'done' || task.status === 'Done';
                  return (
                    <div key={task.id} className="group p-3 flex items-start gap-3 hover:bg-gray-750" data-testid={`pulse-task-${task.id}`}>
                      <button
                        onClick={() => {
                          const newStatus = isDone ? 'Working on it' : 'done';
                          updateTaskStatusMutation.mutate({ taskId: task.id, newStatus });
                        }}
                        className="text-gray-400 hover:text-gray-200 transition-colors mt-0.5 shrink-0"
                        disabled={isEditingThisTask}
                        data-testid={`button-toggle-pulse-task-${task.id}`}
                      >
                        {isDone ? (
                          <CheckSquare className="w-4 h-4 text-green-500" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                      {isEditingThisTask ? (
                        <div className="flex-1 flex gap-2">
                          <Textarea
                            value={editingTaskText}
                            onChange={(e) => setEditingTaskText(e.target.value)}
                            autoFocus
                            className="flex-1 text-sm bg-gray-900 border-gray-600 text-gray-200 min-h-[60px]"
                            data-testid={`textarea-edit-pulse-task-${task.id}`}
                          />
                          <div className="flex flex-col gap-1">
                            <Button
                              onClick={() => handleSaveTaskEdit(task.id)}
                              size="sm"
                              disabled={updateTaskDescriptionMutation.isPending}
                              className="bg-green-600 hover:bg-green-700 h-7 px-2"
                              data-testid={`button-save-pulse-task-${task.id}`}
                            >
                              {updateTaskDescriptionMutation.isPending ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Check className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              onClick={() => {
                                setEditingTaskId(null);
                                setEditingTaskText("");
                              }}
                              variant="outline"
                              size="sm"
                              disabled={updateTaskDescriptionMutation.isPending}
                              className="border-gray-600 h-7 px-2"
                              data-testid={`button-cancel-pulse-task-${task.id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span
                            className={cn(
                              "text-sm flex-1 whitespace-pre-wrap break-words",
                              isDone && "text-gray-500 line-through",
                            )}
                            title={task.taskDescription}
                          >
                            {task.taskDescription || task.title}
                          </span>
                          <Button
                            onClick={() => {
                              setEditingTaskId(task.id);
                              setEditingTaskText(task.taskDescription || task.title || "");
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-blue-400 p-1 h-6 shrink-0 opacity-60 group-hover:opacity-100 focus:opacity-100 md:opacity-0"
                            data-testid={`button-edit-pulse-task-${task.id}`}
                            title="Task bearbeiten"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Staged Tasks Area (Draft List) - always visible for pulses */}
          {isPulse && (
            <div className={cn(
              "border rounded-md bg-gray-800",
              stagedTasks.length > 0 ? "border-amber-600/50" : "border-gray-600"
            )}>
              <div className="p-3 border-b border-gray-600 bg-gray-750 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-2">
                  <List className="w-4 h-4" />
                  Draft Tasks {stagedTasks.length > 0 && `(${stagedTasks.length})`}
                </h3>
                {stagedTasks.length > 0 && (
                  <Button
                    onClick={() => setStagedTasks([])}
                    variant="ghost"
                    size="sm"
                    className="text-xs text-gray-400 hover:text-gray-200"
                    data-testid="button-clear-staged-tasks"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              <div className="divide-y divide-gray-700 max-h-64 overflow-y-auto">
                {stagedTasks.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm italic">
                    Select text in the Full Text area above, then click "Add Selection to Draft".
                  </div>
                ) : stagedTasks.map((task) => (
                    <div
                      key={task.id}
                      className="p-3 hover:bg-gray-750"
                      data-testid={`staged-task-${task.id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          {task.status}
                        </Badge>
                        {task.dueDate && (
                          <span className="text-xs text-gray-400">
                            Due: {task.dueDate.toLocaleDateString()}
                          </span>
                        )}
                        <div className="flex-1" />
                        {editingStagedTaskId !== task.id && (
                          <Button
                            onClick={() => {
                              setEditingStagedTaskId(task.id);
                              setEditingStagedTaskText(task.taskDescription);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-blue-400 p-1 h-6"
                            data-testid={`button-edit-staged-task-${task.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          onClick={() => handleRemoveStagedTask(task.id)}
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-red-400 p-1 h-6"
                          data-testid={`button-remove-staged-task-${task.id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      {editingStagedTaskId === task.id ? (
                        <div className="flex gap-2">
                          <Textarea
                            value={editingStagedTaskText}
                            onChange={(e) => setEditingStagedTaskText(e.target.value)}
                            className="flex-1 text-sm bg-gray-900 border-gray-600 text-gray-200 min-h-[60px]"
                            data-testid={`textarea-edit-staged-task-${task.id}`}
                          />
                          <div className="flex flex-col gap-1">
                            <Button
                              onClick={() => handleSaveStagedTaskEdit(task.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 h-7 px-2"
                              data-testid={`button-save-staged-task-${task.id}`}
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button
                              onClick={() => {
                                setEditingStagedTaskId(null);
                                setEditingStagedTaskText("");
                              }}
                              variant="outline"
                              size="sm"
                              className="border-gray-600 h-7 px-2"
                              data-testid={`button-cancel-staged-task-${task.id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-200 whitespace-pre-wrap" title={task.taskDescription}>
                          {task.taskDescription}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

        </div>
        <DialogFooter>
          {stagedTasks.length > 0 && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleCreateAllStagedTasks();
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="button-create-all-tasks"
            >
              Create Tasks ({stagedTasks.length})
            </Button>
          )}
          {(isEditing || isEditingFullText) ? (
            <Button
              type="button"
              onClick={async () => {
                if (imageUploadPendingRef.current) {
                  toast({
                    title: "Image upload in progress",
                    description: "Wait for the image upload to finish before saving and closing.",
                    variant: "destructive",
                  });
                  return;
                }
                // Save any pending changes before closing
                if (isEditingFullText) {
                  await saveFullTextMutation.mutateAsync(editedFullText);
                }
                if (isEditing) {
                  await saveDescriptionMutation.mutateAsync(detailDescription);
                }
                handleClose();
              }}
              disabled={saveFullTextMutation.isPending || saveDescriptionMutation.isPending || imageUploadPending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="button-save-and-close"
            >
              {(saveFullTextMutation.isPending || saveDescriptionMutation.isPending) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save and Close'
              )}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="button-close-dialog"
            >
              Close
            </Button>
          )}
        </DialogFooter>
        </CallCooperativeDialogContent>
      </CallCooperativeDialog>
    
    {/* Warning dialog when closing with staged tasks */}
    <AlertDialog open={showCloseWarning} onOpenChange={setShowCloseWarning}>
      <AlertDialogContent className="bg-gray-900 border-gray-700">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-white">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Unsaved Staged Tasks
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-300">
            You have {stagedTasks.length} staged task{stagedTasks.length !== 1 ? 's' : ''} that haven't been created yet. 
            What would you like to do?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel 
            onClick={() => setShowCloseWarning(false)}
            className="bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
            data-testid="button-cancel-close"
          >
            Cancel
          </AlertDialogCancel>
          <Button
            onClick={() => {
              setStagedTasks([]);
              if (stagedTasksKey) {
                localStorage.removeItem(stagedTasksKey);
              }
              setShowCloseWarning(false);
              // Now actually close the dialog
              setInternalOpen(false);
              onOpenChange(false);
            }}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
            data-testid="button-discard-tasks"
          >
            Discard Tasks
          </Button>
          <AlertDialogAction
            onClick={() => {
              setShowCloseWarning(false);
              handleCreateAllStagedTasks();
              // Close dialog after creating tasks (handled by auto-close in handleCreateAllStagedTasks)
            }}
            className="bg-green-600 hover:bg-green-700"
            data-testid="button-create-and-close"
          >
            Create Tasks & Close
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}